import React, { useState, useMemo, useCallback, useRef } from 'react';
import {
  Grid,
  InputAdornment,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import classNames from 'classnames';
import { ExpandMore } from '@mui/icons-material';

import { TextField } from 'new/elements/TextField';
import { Search, Clear } from '@mui/icons-material';
import { Virtuoso } from 'react-virtuoso';
import { FeedConfigurationTooltip } from './FeedConfigurationTooltip';
import { SegmentedControl } from 'new/elements/SegmentControl/SegmentControl';

import './ConfigurationFields.scss';

// Import strategy display utilities for v2 templates
import {
  getStrategyShortLabel,
  STRATEGY_DISPLAY_INFO,
} from 'new/components/flows/configure/ELT/ELTSourceSetup/constants/strategies';

// Helper function to get human-readable strategy display names
const getStrategyDisplayName = (strategyName, capabilities = null, templateVersion = 'v1') => {
  // For v2 templates, use the strategy display utilities
  if (templateVersion === 'v2') {
    return (
      getStrategyShortLabel(strategyName) ||
      STRATEGY_DISPLAY_INFO[strategyName]?.label ||
      strategyName
    );
  }

  // For v1 templates, fallback to hardcoded mapping for common cases
  const fallbackMap = {
    auto_increment_strategy: 'Auto Increment',
    full_ingest_strategy: 'Full Ingest',
    base_auto: 'Auto Increment',
    base_full: 'Full Ingest',
    incremental: 'Incremental',
    full: 'Full Ingest',
  };

  return fallbackMap[strategyName] || strategyName;
};

// Helper function to extract vendor information for display purposes
const extractVendorInfoForDisplay = (vendors, data, vendorEndpoints, vendorEndpointParams) => {
  if (!vendors || !data?.vendor_id) {
    return null;
  }

  const vendor = vendors[data.vendor_id];
  if (!vendor) {
    return null;
  }

  // Check if this is a v2 template
  const isV2Template =
    vendor.config?.template_type === 'v2' ||
    (vendorEndpoints &&
      vendor.vendor_endpoints?.some(
        (id) => vendorEndpoints[id]?.source_template?.template_type === 'v2',
      ));

  // For v2 templates, get the feeds endpoint and its data
  let feedsEndpoint = null;
  let feedStrategyMappingParam = null;
  let capabilities = null;

  if (isV2Template && vendorEndpoints && vendor.vendor_endpoints) {
    // Get the feeds endpoint (the one with endpoint_type === 'feeds')
    feedsEndpoint = vendor.vendor_endpoints
      .map((id) => vendorEndpoints[id])
      .find((ep) => ep?.source_template?.endpoint_type === 'feeds');

    if (feedsEndpoint) {
      capabilities = feedsEndpoint.config?.capabilities;

      // Get the feed_strategy_mapping parameter from the endpoint's resource_parameters
      // resource_parameters are IDs, so we need to look them up in vendorEndpointParams
      if (feedsEndpoint.resource_parameters && vendorEndpointParams) {
        const resolvedParams = feedsEndpoint.resource_parameters
          .map((paramId) => vendorEndpointParams[paramId])
          .filter(Boolean);

        feedStrategyMappingParam = resolvedParams.find(
          (param) => param?.config?.param_type === 'feed_strategy_mapping',
        );
      }

      // Fallback: if endpoint resource_parameters is empty, look directly in vendorEndpointParams
      if (!feedStrategyMappingParam && vendorEndpointParams) {
        const allParams = Object.values(vendorEndpointParams);
        feedStrategyMappingParam = allParams.find(
          (param) => param?.config?.param_type === 'feed_strategy_mapping',
        );
      }
    }
  }

  return {
    vendor,
    isV2Template,
    capabilities,
    feedsEndpoint,
    feedStrategyMappingParam,
  };
};

// Optimized table row component for better performance
const VirtualizedTableRow = React.memo(
  ({ feed, index, syncModeLookup, templateVersion = 'v1', capabilities = null }) => {
    // Minimal data extraction - only what's needed for display
    const displayName = feed.label || '';
    const feedCodeName = feed.value || '';
    const refSpec = feed.feedProps?.spec?.ref_spec;
    const lastIngested = feed.feedProps?.last_ingested;
    const lastDetected = feed.feedProps?.detected;
    const isAutoDetected = !!lastDetected; // Show signal if detected property exists

    // Memoized display values to prevent recalculation - now using the lookup map
    const syncModeDisplay = useMemo(() => {
      if (!refSpec) return '';

      // Use the pre-computed lookup map for O(1) performance
      const strategyDisplay = syncModeLookup.get(refSpec);
      if (strategyDisplay) {
        return strategyDisplay;
      }

      // Fallback: try to infer strategy from ref_spec value itself
      const inferredStrategy = getStrategyDisplayName(refSpec, capabilities, templateVersion);
      if (inferredStrategy !== refSpec) {
        return inferredStrategy;
      }

      // Final fallback to ref_spec if no mapping found
      return refSpec;
    }, [refSpec, syncModeLookup, templateVersion, capabilities]);

    const fieldSelectionDisplay = useMemo(() => {
      const forceAttrs = feed.feedProps?.spec?.params?.['feed_spec.nx_attr_force_attrs'];
      const forceAttrsEnabled = feed.feedProps?.spec?.params?.['feed_spec.nx_attr_force'];
      const allFieldsLength = feed.feedProps?.spec?.params?.['ui.all_fields_length'];

      // For v2 templates, also check for different parameter names
      let v2ForceAttrs, v2ForceAttrsEnabled, v2AllFieldsLength;
      if (templateVersion === 'v2') {
        v2ForceAttrs = feed.feedProps?.spec?.params?.['ui.selected_fields'];
        v2ForceAttrsEnabled = feed.feedProps?.spec?.params?.['ui.field_selection_enabled'];
        v2AllFieldsLength = feed.feedProps?.spec?.params?.['ui.total_fields'];
      }

      // Use v2 parameters if available, otherwise fall back to v1
      const effectiveForceAttrs = v2ForceAttrs || forceAttrs;
      const effectiveForceAttrsEnabled =
        v2ForceAttrsEnabled !== undefined ? v2ForceAttrsEnabled : forceAttrsEnabled;
      const effectiveAllFieldsLength = v2AllFieldsLength || allFieldsLength;

      if (effectiveForceAttrsEnabled && effectiveForceAttrs) {
        let fieldCount;
        if (typeof effectiveForceAttrs === 'string') {
          fieldCount = effectiveForceAttrs.split(',').length;
        } else if (typeof effectiveForceAttrs === 'object') {
          fieldCount = Object.keys(effectiveForceAttrs).length;
        } else {
          fieldCount = 1;
        }
        return `${fieldCount} selected fields`;
      } else if (effectiveAllFieldsLength) {
        return `All ${effectiveAllFieldsLength} fields`;
      }
      return 'All fields';
    }, [feed.feedProps?.spec?.params, templateVersion]);

    const lastIngestedDisplay = useMemo(() => {
      if (!lastIngested) return '';
      const date = new Date(lastIngested);
      return date.toLocaleString();
    }, [lastIngested]);

    const lastDetectedDisplay = useMemo(() => {
      if (!lastDetected) return '';
      const date = new Date(lastDetected);
      return date.toLocaleString();
    }, [lastDetected]);

    // Field list for tooltip
    const fieldList = useMemo(() => {
      const forceAttrs = feed.feedProps?.spec?.params?.['feed_spec.nx_attr_force_attrs'];
      const selectedFields = feed.feedProps?.spec?.params?.['ui.selected_fields'];

      // For v2 templates, also check for different parameter names
      let v2ForceAttrs, v2SelectedFields;
      if (templateVersion === 'v2') {
        v2ForceAttrs = feed.feedProps?.spec?.params?.['ui.selected_fields'];
        v2SelectedFields = feed.feedProps?.spec?.params?.['ui.field_mappings'];
      }

      // Use v2 parameters if available, otherwise fall back to v1
      const effectiveForceAttrs = v2ForceAttrs || forceAttrs;
      const effectiveSelectedFields = v2SelectedFields || selectedFields;

      if (effectiveForceAttrs) {
        if (typeof effectiveForceAttrs === 'string') {
          return effectiveForceAttrs.split(',').map((field) => field.trim());
        } else if (typeof effectiveForceAttrs === 'object') {
          return Object.keys(effectiveForceAttrs).map((fieldName) => {
            const displayName = effectiveForceAttrs[fieldName];
            return `${fieldName}:${displayName}`;
          });
        }
      } else if (effectiveSelectedFields && typeof effectiveSelectedFields === 'object') {
        return Object.keys(effectiveSelectedFields).map((fieldName) => {
          const displayName = effectiveSelectedFields[fieldName];
          return `${fieldName}:${displayName}`;
        });
      }
      return null;
    }, [feed.feedProps?.spec?.params, templateVersion]);

    // Helper function to determine if feed is skipped
    const isFeedSkipped = useMemo(() => {
      return (
        feed.feedProps?.spec?.skip_feed === true || feed.feedProps?.spec?.feed_selected === false
      );
    }, [feed.feedProps?.spec?.skip_feed, feed.feedProps?.spec?.feed_selected]);

    const feedConfigTooltipInfo = useMemo(
      () => ({
        label: displayName,
        codeName: feedCodeName,
        syncMode: syncModeDisplay,
        primaryKeys: feed.feedProps?.spec?.['primary.keys'],
        forceResync: feed.feedProps?.spec?.force_resync,
        fieldSelection: fieldSelectionDisplay,
        selectedFields: fieldList ? fieldList.join(', ') : undefined,
        fieldMappings: feed.feedProps?.spec?.params?.['ui.selected_fields'],
        totalAvailableFields: feed.feedProps?.spec?.params?.['ui.all_fields_length'],
        lastIngested: lastIngestedDisplay,
        detected: lastDetectedDisplay,
      }),
      [
        displayName,
        feedCodeName,
        syncModeDisplay,
        feed.feedProps?.spec?.['primary.keys'],
        feed.feedProps?.spec?.force_resync,
        fieldSelectionDisplay,
        fieldList,
        feed.feedProps?.spec?.params?.['ui.selected_fields'],
        feed.feedProps?.spec?.params?.['ui.all_fields_length'],
        lastIngestedDisplay,
        lastDetectedDisplay,
      ],
    );

    return (
      <Grid
        container
        key={`feed-${feed.value || index}`}
        className={classNames('table-body-row', {
          'feed-skipped': isFeedSkipped,
        })}
      >
        <Grid item xs={3}>
          <FeedConfigurationTooltip feedData={feedConfigTooltipInfo}>
            <div className="table-cell-container">
              <span className="display-name">
                {displayName.length > 25 ? displayName.substring(0, 25) + '...' : displayName}
              </span>
            </div>
          </FeedConfigurationTooltip>
        </Grid>
        <Grid item xs={3}>
          <FeedConfigurationTooltip
            feedData={feedConfigTooltipInfo}
            className="table-cell-container"
          >
            <div className="table-cell-container">
              <span className="feed-code-name">
                {feedCodeName.length > 25 ? feedCodeName.substring(0, 25) + '...' : feedCodeName}
              </span>
            </div>
          </FeedConfigurationTooltip>
        </Grid>
        <Grid item xs={2}>
          <FeedConfigurationTooltip feedData={feedConfigTooltipInfo}>
            <div className="table-cell-container">
              <span className="sync-mode">
                {syncModeDisplay.length > 12
                  ? syncModeDisplay.substring(0, 12) + '...'
                  : syncModeDisplay}
              </span>
            </div>
          </FeedConfigurationTooltip>
        </Grid>
        <Grid item xs={1}>
          <FeedConfigurationTooltip feedData={feedConfigTooltipInfo}>
            <div className="table-cell-container">
              <span className="field-selection">{fieldSelectionDisplay}</span>
            </div>
          </FeedConfigurationTooltip>
        </Grid>
        <Grid item xs={1.5}>
          <FeedConfigurationTooltip feedData={feedConfigTooltipInfo}>
            <div className="table-cell-container">
              <span className="last-ingested">{lastIngestedDisplay}</span>
            </div>
          </FeedConfigurationTooltip>
        </Grid>
        <Grid item xs={1.5}>
          <FeedConfigurationTooltip feedData={feedConfigTooltipInfo}>
            <div className="auto-detected-indicator table-cell-container">
              {isAutoDetected && <div className="auto-detected-dot" title="Auto-detected feed" />}
            </div>
          </FeedConfigurationTooltip>
        </Grid>
      </Grid>
    );
  },
);

/**
 * FeedsListWithSearch component for displaying and filtering a list of data feeds
 * @param {Object} props
 * @param {Array} props.feedsList - Array of feed objects
 * @param {Array} props.configFields - Configuration fields for the feeds
 * @param {boolean} props.isExpanded - Whether the accordion is expanded
 * @param {Function} props.onAccordionToggle - Callback for accordion toggle
 * @param {string} props.templateVersion - Template version ('v1' or 'v2')
 * @param {Object} props.data - Source data object for template version detection
 */
const FeedsListWithSearch = ({
  feedsList,
  configFields,
  isExpanded,
  onAccordionToggle,
  templateVersion = 'v1',
  data,
  vendors,
  sourceConfigById,
  vendorEndpoints,
  vendorEndpointParams,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [sortColumn, setSortColumn] = useState('value');
  const [sortDirection, setSortDirection] = useState('asc');
  const [showOnlySelected, setShowOnlySelected] = useState(true);
  const [showOnlyAutoDetected, setShowOnlyAutoDetected] = useState(false);
  const containerRef = useRef(null);
  const virtuosoRef = useRef(null);

  // Calculate feed counts locally - no need to pass them around
  const feedCounts = useMemo(() => {
    let selected = 0;
    let skipped = 0;
    let autoDetected = 0;

    feedsList.forEach((feed) => {
      const spec = feed.feedProps?.spec;
      if (spec?.feed_selected === false || spec?.skip_feed === true) {
        skipped++;
      } else {
        selected++;
      }
      if (feed.feedProps?.detected) {
        autoDetected++;
      }
    });

    return { selected, skipped, autoDetected, total: feedsList.length };
  }, [feedsList]);

  // Pre-compute sync mode lookup map for O(1) access during sorting
  const syncModeLookup = useMemo(() => {
    const lookup = new Map();
    let capabilities = null;

    // Extract vendor info for display
    const vendorInfo = extractVendorInfoForDisplay(
      vendors,
      data,
      vendorEndpoints,
      vendorEndpointParams,
    );

    if (vendorInfo?.isV2Template) {
      capabilities = vendorInfo.capabilities;

      // For v2 templates, create mapping from ref_feed to strategy display name
      if (vendorInfo.feedStrategyMappingParam?.config) {
        const strategyMapping = vendorInfo.feedStrategyMappingParam.config;

        // Create reverse mapping: ref_feed -> strategy display name
        Object.entries(strategyMapping).forEach(([strategyName, refFeeds]) => {
          if (Array.isArray(refFeeds) && strategyName !== 'param_type') {
            refFeeds.forEach((refFeed) => {
              // Use the strategy display utilities to get the proper label
              const displayName =
                getStrategyShortLabel(strategyName) ||
                STRATEGY_DISPLAY_INFO[strategyName]?.label ||
                strategyName;
              lookup.set(refFeed, displayName);
            });
          }
        });
      }
    } else {
      // For v1 templates, use traditional feed_types field
      if (configFields && Array.isArray(configFields)) {
        const feedTypesField = configFields.find(
          (field) => field && field.param_type === 'feed_types',
        );

        if (feedTypesField && Array.isArray(feedTypesField.options)) {
          feedTypesField.options.forEach((option) => {
            if (option && typeof option.value !== 'undefined' && option.label) {
              lookup.set(option.value, option.label);
            }
          });
        }
      }
    }

    return { lookup, capabilities };
  }, [configFields, templateVersion, data, vendors, vendorEndpoints, vendorEndpointParams]);

  // Get sync mode display for sorting - now O(1) instead of O(n)
  const getSyncModeDisplay = useCallback(
    (refSpec) => {
      if (!refSpec) return '';

      // Use the pre-computed lookup map for O(1) performance
      return syncModeLookup.lookup.get(refSpec) || refSpec;
    },
    [syncModeLookup],
  );

  // Optimized sorting function - faster and non-blocking
  const sortFeeds = useCallback(
    (feeds, column, direction) => {
      const sortedFeeds = [...feeds];

      return sortedFeeds.sort((a, b) => {
        let aValue = '';
        let bValue = '';

        switch (column) {
          case 'label':
            aValue = (a.label || '').replace(/^[_\-\s]+/, '').toLowerCase();
            bValue = (b.label || '').replace(/^[_\-\s]+/, '').toLowerCase();
            break;
          case 'value':
            aValue = (a.value || '').toLowerCase();
            bValue = (b.value || '').toLowerCase();
            break;
          case 'syncMode':
            aValue = getSyncModeDisplay(a.feedProps?.spec?.ref_spec).toLowerCase();
            bValue = getSyncModeDisplay(b.feedProps?.spec?.ref_spec).toLowerCase();
            break;
          case 'lastIngested':
            aValue = a.feedProps?.last_ingested || '';
            bValue = b.feedProps?.last_ingested || '';
            break;
          default:
            aValue = (a.value || '').toLowerCase();
            bValue = (b.value || '').toLowerCase();
        }

        if (direction === 'asc') {
          return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
        } else {
          return bValue < aValue ? -1 : bValue > aValue ? 1 : 0;
        }
      });
    },
    [getSyncModeDisplay],
  );

  // Handle column sorting
  const handleColumnSort = useCallback(
    (column) => {
      if (sortColumn === column) {
        setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
      } else {
        setSortColumn(column);
        setSortDirection('asc');
      }
    },
    [sortColumn, sortDirection],
  );

  // Helper function to check if a feed matches search term
  const isFeedMatchingSearch = useCallback((feed, searchLower) => {
    const displayName = feed.label?.toLowerCase() || '';
    const feedName = feed.value?.toLowerCase() || '';

    return displayName.includes(searchLower) || feedName.includes(searchLower);
  }, []);

  // Helper function to check if a feed is selected (will be ingested)
  const isFeedSelected = useCallback((feed) => {
    const spec = feed.feedProps?.spec;
    return spec && spec.feed_selected !== false && spec.skip_feed !== true;
  }, []);

  // Helper function to check if a feed is auto-detected
  const isFeedAutoDetected = useCallback((feed) => {
    return !!feed.feedProps?.detected;
  }, []);

  // Immediate search with optimized filtering and sorting
  const filteredAndSortedFeeds = useMemo(() => {
    // Start with the base feeds list
    let filtered = feedsList;

    // Apply selected feeds filter (user selected + auto-detected)
    if (showOnlySelected) {
      filtered = feedsList.filter(isFeedSelected);
    }

    // Apply auto-detected only filter
    if (showOnlyAutoDetected) {
      filtered = feedsList.filter(isFeedAutoDetected);
    }

    // Apply search filter
    if (searchTerm.trim()) {
      const searchLower = searchTerm.trim().toLowerCase();

      filtered = filtered.filter((feed) => {
        return isFeedMatchingSearch(feed, searchLower);
      });
    }

    // Sort by selected column and direction
    return sortFeeds(filtered, sortColumn, sortDirection);
  }, [
    feedsList,
    searchTerm,
    showOnlySelected,
    showOnlyAutoDetected,
    sortColumn,
    sortDirection,
    sortFeeds,
    isFeedSelected,
    isFeedAutoDetected,
    isFeedMatchingSearch,
  ]);

  // Helper function to get search results info text
  const getSearchResultsInfo = useCallback(() => {
    if (!searchTerm.trim() || !feedsList.length) return null;

    const totalCount = showOnlySelected
      ? feedCounts.selected || 0
      : showOnlyAutoDetected
        ? feedCounts.autoDetected || 0
        : feedCounts.total;

    const feedType = showOnlySelected
      ? 'selected feeds'
      : showOnlyAutoDetected
        ? 'auto-detected feeds'
        : 'available feeds';

    return `Showing ${filteredAndSortedFeeds.length} of ${totalCount} ${feedType}`;
  }, [
    searchTerm,
    showOnlySelected,
    showOnlyAutoDetected,
    feedCounts.selected,
    feedCounts.autoDetected,
    feedCounts.total,
    filteredAndSortedFeeds.length,
  ]);

  // Helper function to get search placeholder text
  const getSearchPlaceholder = useCallback(() => {
    if (showOnlySelected) return 'Search selected feeds...';
    if (showOnlyAutoDetected) return 'Search auto-detected feeds...';
    return 'Search all feeds...';
  }, [showOnlySelected, showOnlyAutoDetected]);

  // Proper virtualization with Virtuoso
  const itemContent = useCallback(
    (index, feed) => {
      return (
        <VirtualizedTableRow
          key={`feed-${feed.value || index}`}
          feed={feed}
          index={index}
          syncModeLookup={syncModeLookup.lookup}
          templateVersion={templateVersion}
          capabilities={syncModeLookup.capabilities}
        />
      );
    },
    [syncModeLookup, templateVersion],
  );

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleClearSearch = () => {
    setSearchTerm('');
  };

  return (
    <div className="details-accordion-group" aria-label="details-accordion-group-container">
      {/* Accordion Header - Self-contained */}
      <Accordion
        className="accordion-item"
        key="feeds-accordion"
        expanded={isExpanded}
        onChange={onAccordionToggle}
      >
        <AccordionSummary className="accordion-title" expandIcon={<ExpandMore fontSize="large" />}>
          <div className="accordion-title-content">
            <div className="accordion-content-grow">
              <span className="title">Feeds</span>
            </div>
            <div className="feeds-subtitle-above">
              {feedCounts.selected} selected • {feedCounts.skipped} skipped
            </div>
            <div className="toggle-text" />
          </div>
        </AccordionSummary>

        <AccordionDetails className="accordion-body">
          <div className="feeds-list-wrapper">
            {/* Subtitle with counts */}
            {/* <div className="feeds-subtitle-above">
              {feedCounts.selected} selected (will be ingested) •{' '}
              {feedCounts.skipped} skipped
            </div> */}

            <div className="feeds-list-container">
              {/* Search Input and Filter Controls */}
              <div className="feeds-search-container">
                <div className="search-controls-inline">
                  {/* Search field */}
                  <div className="search-field-container">
                    <TextField
                      variant="outlined"
                      size="small"
                      placeholder={getSearchPlaceholder()}
                      value={searchTerm}
                      onChange={handleSearchChange}
                      className="search-field"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Search fontSize="small" />
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <InputAdornment position="end">
                            {searchTerm && (
                              <IconButton size="small" onClick={handleClearSearch} edge="end">
                                <Clear fontSize="small" />
                              </IconButton>
                            )}
                          </InputAdornment>
                        ),
                      }}
                    />
                  </div>

                  {/* Auto-detected filter */}
                  <div className="auto-detected-filter">
                    <SegmentedControl
                      controls={[
                        {
                          label: `Selected (${feedCounts.selected || 0})`,
                          value: 'selected',
                        },
                        {
                          label: `Auto-detected only (${feedCounts.autoDetected || 0})`,
                          value: 'auto-detected',
                        },
                        {
                          label: `All Feeds (${feedCounts.total || 0})`,
                          value: 'all',
                        },
                      ]}
                      defaultValue="selected"
                      value={
                        showOnlySelected
                          ? 'selected'
                          : showOnlyAutoDetected
                            ? 'auto-detected'
                            : 'all'
                      }
                      onControlChange={(_, value) => {
                        setShowOnlySelected(value === 'selected');
                        setShowOnlyAutoDetected(value === 'auto-detected');
                      }}
                    />
                  </div>
                </div>

                {/* Results count positioned below search controls - always present to prevent jumping */}
                {getSearchResultsInfo() && (
                  <div className="search-results-info">{getSearchResultsInfo()}</div>
                )}
              </div>

              {/* Feeds Table */}
              <div className="full-width-table">
                <div className="table-header">
                  <Grid container className="table-header-row">
                    <Grid item xs={3}>
                      <div className="sortable-header" onClick={() => handleColumnSort('label')}>
                        Display Name
                        {sortColumn === 'label' && (
                          <span className="sort-indicator">
                            {sortDirection === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </div>
                    </Grid>
                    <Grid item xs={3}>
                      <div className="sortable-header" onClick={() => handleColumnSort('value')}>
                        Code Name
                        {sortColumn === 'value' && (
                          <span className="sort-indicator">
                            {sortDirection === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </div>
                    </Grid>
                    <Grid item xs={2}>
                      <div className="sortable-header" onClick={() => handleColumnSort('syncMode')}>
                        Sync Mode
                        {sortColumn === 'syncMode' && (
                          <span className="sort-indicator">
                            {sortDirection === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </div>
                    </Grid>
                    <Grid item xs={1}>
                      <div className="header-text">Fields</div>
                    </Grid>
                    <Grid item xs={1.5}>
                      <div
                        className="sortable-header"
                        onClick={() => handleColumnSort('lastIngested')}
                      >
                        Last Ingested
                        {sortColumn === 'lastIngested' && (
                          <span className="sort-indicator">
                            {sortDirection === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </div>
                    </Grid>
                    <Grid item xs={1.5}>
                      <div className="header-text">Auto Detected</div>
                    </Grid>
                  </Grid>
                </div>
                <div className="table-body" ref={containerRef}>
                  <Virtuoso
                    ref={virtuosoRef}
                    key="feeds-list-table"
                    data={filteredAndSortedFeeds}
                    itemContent={itemContent}
                    customScrollParent={containerRef.current}
                    className="virtuoso-container"
                    style={{ height: '400px' }}
                    components={{
                      Footer: () => <div className="virtuoso-footer"></div>,
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </AccordionDetails>
      </Accordion>
    </div>
  );
};

export default FeedsListWithSearch;
